import {summarizePaymentInstructions} from '@sollapay/domain/rules';
import {PaymentInstructionStatusGroup} from '@sollapay/enums';
import {EmptyState, Illustration} from '@sollapay/ui';
import {Button, ButtonGroup, ContentSection, Icon, ListEmptySearch} from '@sollapay/ui/components';
import {SearchIllustration} from '@sollapay/ui/illustrations';
import {useCallback, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {ConfirmActionModal} from '@/components';
import {
  useBuyersQuery,
  useCancelPaymentInstructionMutation,
  usePaymentsQuery,
  useResendPaymentInstructionMutation,
  useSetPaymentInstructionHandledMutation
} from '@/features/buyers/hooks';
import {InitiatePaymentFlow} from '@/features/buyers/modals';
import {formatBuyerDisplayName, formatPurchaseAssetNames} from '@/features/buyers/utils';
import {showPaymentInstructionActionToast} from '@/features/buyers/view/components';
import {PURCHASE_PARAM} from '@/features/buyers/view/hooks';
import {
  useCurrentTrustAccount,
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';
import {useCanCreatePaymentRequest} from '@/features/trust-accounts/view/hooks';

import {DEFAULT_INSTRUCTIONS_FILTER_STATE} from './config/instructions-filter.config';
import {InstructionsEmptyState} from './InstructionsEmptyState';
import {InstructionsFilter} from './InstructionsFilter';
import {InstructionsKpiCards} from './InstructionsKpiCards';
import {InstructionsTabContentSkeleton} from './InstructionsTabContent.skeleton';
import {InstructionsTable} from './InstructionsTable';
import {
  filterInstructions,
  filterInstructionsByGroup,
  isInstructionsFilterActive
} from './utils/instructions-filter.utils';

import type {InstructionsFilterState} from './config/instructions-filter.config';
import type {PaymentInstructionDTO} from '@sollapay/types';
import type {ButtonGroupItemDef} from '@sollapay/ui/components';
import type {FC} from 'react';

/** One icon per pill shown in `groupItems`. */
const PILL_GROUP_ICON = {
  [PaymentInstructionStatusGroup.IN_PROGRESS]: 'clock',
  [PaymentInstructionStatusGroup.NEEDS_ATTENTION]: 'alert-circle',
  [PaymentInstructionStatusGroup.COMPLETED]: 'check-circle'
} as const;

/** Calls out a non-zero Needs Attention count — every other pill stays neutral. */
function pillBadgeVariant(
  group: PaymentInstructionStatusGroup,
  count: number
): 'warning' | undefined {
  return group === PaymentInstructionStatusGroup.NEEDS_ATTENTION && count > 0
    ? 'warning'
    : undefined;
}

export const InstructionsTabContent: FC = () => {
  const {t} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {canCollectPayments, canResendPayments} = useTrustAccountPermissions();
  const canCreatePaymentRequest = useCanCreatePaymentRequest();
  const {allowsMultiplePurchases} = useTrustTypeStrategy();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PaymentInstructionDTO | null>(null);
  const [filter, setFilter] = useState<InstructionsFilterState>(DEFAULT_INSTRUCTIONS_FILTER_STATE);
  const [activeGroup, setActiveGroup] = useState<PaymentInstructionStatusGroup | 'all'>(
    PaymentInstructionStatusGroup.IN_PROGRESS
  );

  const {data: instructions = [], isLoading: instructionsLoading} =
    usePaymentsQuery(trustAccountId);
  const {data: buyers = [], isLoading: buyersLoading} = useBuyersQuery(trustAccountId);
  const resend = useResendPaymentInstructionMutation(trustAccountId);
  const cancel = useCancelPaymentInstructionMutation(trustAccountId);
  const {mutateAsync: mutateHandled} = useSetPaymentInstructionHandledMutation(trustAccountId);
  const handledInFlightIds = useRef(new Set<string>());

  const buyersById = useMemo(() => new Map(buyers.map(b => [b.id, b])), [buyers]);

  // ponytail: derived from buyers' own purchases, so a purchase with zero buyers (a valid,
  // standing state — see BuyersService.delete) is invisible here: its instructions show "—" for
  // asset and can't be asset-filtered. No "list all purchases for a trust account" endpoint
  // exists yet to fix this properly; add one if buyer-less purchases turn out to be common.
  const assetNameByPurchaseId = useMemo(() => {
    const map = new Map<string, string>();
    buyers.forEach(buyer =>
      buyer.purchases.forEach(purchase => {
        if (!map.has(purchase.id))
          map.set(purchase.id, formatPurchaseAssetNames(purchase.assets, t));
      })
    );
    return map;
  }, [buyers, t]);

  const purchaseOptions = useMemo(
    () => Array.from(assetNameByPurchaseId, ([id, label]) => ({id, label})),
    [assetNameByPurchaseId]
  );
  const buyerOptions = useMemo(
    () => buyers.map(buyer => ({id: buyer.id, label: formatBuyerDisplayName(buyer.fullName)})),
    [buyers]
  );

  // Always reflects the whole account, independent of the table's active filters.
  const kpiSummary = useMemo(() => summarizePaymentInstructions(instructions), [instructions]);

  const filtered = useMemo(() => filterInstructions(instructions, filter), [instructions, filter]);
  // Pill badge counts respect every filter but the group, so switching pills never hides a count.
  const pillSummary = useMemo(() => summarizePaymentInstructions(filtered), [filtered]);
  const rows = useMemo(
    () => filterInstructionsByGroup(filtered, activeGroup),
    [filtered, activeGroup]
  );
  const isNeedsAttentionGroup = activeGroup === PaymentInstructionStatusGroup.NEEDS_ATTENTION;

  const handleClearFilters = useCallback(() => {
    setFilter(DEFAULT_INSTRUCTIONS_FILTER_STATE);
    setActiveGroup('all');
  }, []);

  const handleResend = useCallback(
    (instruction: PaymentInstructionDTO) => resend.mutate(instruction.id),
    [resend]
  );

  const handleCancel = useCallback((instruction: PaymentInstructionDTO) => {
    setCancelTarget(instruction);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (!cancelTarget) return;
    cancel.mutate(cancelTarget.id, {
      onSuccess: () => {
        setCancelTarget(null);
        showPaymentInstructionActionToast('cancelSuccess');
      },
      onError: () => {
        setCancelTarget(null);
        showPaymentInstructionActionToast('cancelError');
      }
    });
  }, [cancel, cancelTarget]);

  const handleSetHandled = useCallback(
    (instruction: PaymentInstructionDTO, handled: boolean) => {
      const inFlight = handledInFlightIds.current;
      if (inFlight.has(instruction.id)) return;
      inFlight.add(instruction.id);
      void mutateHandled({paymentId: instruction.id, handled})
        .catch(() => showPaymentInstructionActionToast('handledError'))
        .finally(() => inFlight.delete(instruction.id));
    },
    [mutateHandled]
  );

  const handleViewBuyer = useCallback(
    (buyerId: string, trustBuyerPurchaseId: string) => {
      navigate(
        `/trust-accounts/${trustAccountId}/buyers/${buyerId}?${PURCHASE_PARAM}=${trustBuyerPurchaseId}`
      );
    },
    [navigate, trustAccountId]
  );

  const isLoading = instructionsLoading || buyersLoading;
  if (isLoading) {
    return <InstructionsTabContentSkeleton showsAssetColumn={allowsMultiplePurchases} />;
  }

  const isEmpty = instructions.length === 0;

  const groupItems: ButtonGroupItemDef[] = [
    ...(
      [
        PaymentInstructionStatusGroup.IN_PROGRESS,
        PaymentInstructionStatusGroup.NEEDS_ATTENTION,
        PaymentInstructionStatusGroup.COMPLETED
      ] as const
    ).map(group => ({
      value: group,
      label: t(`view.instructions.groups.${group}`),
      icon: PILL_GROUP_ICON[group],
      badge: pillSummary.counts[group],
      badgeVariant: pillBadgeVariant(group, pillSummary.counts[group])
    })),
    {
      value: 'all',
      label: t('view.instructions.groups.all'),
      icon: 'menu',
      badge: pillSummary.counts.all
    }
  ];

  return (
    <>
      {isEmpty ? (
        <InstructionsEmptyState
          onCreate={canCreatePaymentRequest ? () => setIsCreateOpen(true) : undefined}
        />
      ) : (
        <ContentSection
          title={t('view.instructions.title')}
          subtitle={t('view.instructions.subtitle')}
          action={
            canCreatePaymentRequest && (
              <Button
                variant="primary"
                size="sm"
                icon={<Icon name="add" width={16} height={16} />}
                onClick={() => setIsCreateOpen(true)}
              >
                {t('view.instructions.newInstruction')}
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-4">
            <InstructionsKpiCards
              summary={kpiSummary}
              activeGroup={activeGroup}
              onSelectGroup={setActiveGroup}
            />

            <div className="flex items-center justify-between gap-3">
              <ButtonGroup
                items={groupItems}
                value={activeGroup}
                onChange={value => setActiveGroup(value as PaymentInstructionStatusGroup | 'all')}
              />
              <InstructionsFilter
                filter={filter}
                onChange={setFilter}
                purchaseOptions={allowsMultiplePurchases ? purchaseOptions : []}
                buyerOptions={buyerOptions}
              />
            </div>

            {rows.length === 0 && (activeGroup === 'all' || isInstructionsFilterActive(filter)) ? (
              <ListEmptySearch
                title={t('view.instructions.emptySearch.title')}
                subtitle={t('view.instructions.emptySearch.subtitle')}
                clearLabel={t('view.instructions.emptySearch.clear')}
                onClear={handleClearFilters}
              />
            ) : (
              <InstructionsTable
                instructions={rows}
                onSetHandled={canCollectPayments ? handleSetHandled : undefined}
                buyersById={buyersById}
                assetNameByPurchaseId={assetNameByPurchaseId}
                showAssetColumn={allowsMultiplePurchases}
                emptyState={
                  activeGroup !== 'all' && (
                    <EmptyState
                      size="compact"
                      illustration={
                        <Illustration width={166} height={160}>
                          <SearchIllustration />
                        </Illustration>
                      }
                      title={t(`view.instructions.emptyGroup.${activeGroup}`)}
                    />
                  )
                }
                onResend={canResendPayments ? handleResend : undefined}
                onCancel={canCollectPayments ? handleCancel : undefined}
                groupHandled={isNeedsAttentionGroup}
                onViewBuyer={handleViewBuyer}
              />
            )}
          </div>
        </ContentSection>
      )}

      <InitiatePaymentFlow
        trustAccountId={trustAccountId}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <ConfirmActionModal
        isOpen={!!cancelTarget}
        icon="credit-card-cancel"
        iconVariant="destructive"
        primaryVariant="destructive"
        title={t('view.buyers.detail.cancelPayment.title')}
        description={t('view.buyers.detail.cancelPayment.description', {
          name: cancelTarget
            ? (buyersById.get(cancelTarget.buyerPartyId ?? '')?.fullName ??
              t('view.buyers.detail.cancelPayment.unknownBuyer'))
            : ''
        })}
        confirmLabel={t('view.buyers.detail.cancelPayment.confirmLabel')}
        isLoading={cancel.isPending}
        onConfirm={handleConfirmCancel}
        onClose={() => setCancelTarget(null)}
      />
    </>
  );
};
